import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { 
  Plus, Trash2, Clock, Video, MessageSquare,
  ChevronDown, ChevronUp, X
} from 'lucide-react';

interface Question {
  id: string;
  question: string;
  type: string;
  isRequired: boolean;
  options: string[];
  crmField?: string;
}

interface FormData {
  name: string;
  slug: string;
  description: string;
  duration: number;
  bufferBefore: number;
  bufferAfter: number;
  timezone: string;
  autoCreateMeetingLink: boolean;
  meetingProvider: string;
  discordWebhookUrl: string;
  discordChannel: string;
  questions: Question[];
}

const QUESTION_TYPES = [
  { value: 'TEXT', label: 'Short Text' },
  { value: 'TEXTAREA', label: 'Long Text' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'SELECT', label: 'Dropdown' },
  { value: 'MULTI_SELECT', label: 'Multi Select' },
  { value: 'NUMBER', label: 'Number' },
];

const CRM_FIELDS = [
  { value: '', label: 'None' },
  { value: 'company_size', label: 'Company Size' },
  { value: 'industry', label: 'Industry' },
  { value: 'budget', label: 'Budget Range' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'use_case', label: 'Use Case' },
];

export default function TemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    description: '',
    duration: 30,
    bufferBefore: 0,
    bufferAfter: 0,
    timezone: 'UTC',
    autoCreateMeetingLink: true,
    meetingProvider: 'GOOGLE_MEET',
    discordWebhookUrl: '',
    discordChannel: '',
    questions: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (isEditing) {
      loadTemplate();
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await api.getMeetingTemplate(id!) as { template: any };
      setFormData({
        name: data.template.name,
        slug: data.template.slug,
        description: data.template.description || '',
        duration: data.template.duration,
        bufferBefore: data.template.bufferBefore,
        bufferAfter: data.template.bufferAfter,
        timezone: data.template.timezone,
        autoCreateMeetingLink: data.template.autoCreateMeetingLink,
        meetingProvider: data.template.meetingProvider,
        discordWebhookUrl: data.template.discordWebhookUrl || '',
        discordChannel: data.template.discordChannel || '',
        questions: data.template.questions.map((q: any) => ({
          id: q.id,
          question: q.question,
          type: q.type,
          isRequired: q.isRequired,
          options: q.options || [],
          crmField: q.crmField,
        })),
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');

      if (isEditing) {
        await api.updateMeetingTemplate(id!, formData);
      } else {
        await api.createMeetingTemplate(formData);
      }

      navigate('/templates');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now().toString(),
      question: '',
      type: 'TEXT',
      isRequired: false,
      options: [],
    };
    setFormData({ ...formData, questions: [...formData.questions, newQuestion] });
  };

  const updateQuestion = (index: number, updates: Partial<Question>) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], ...updates };
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeQuestion = (index: number) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: newQuestions });
  };

  const addOption = (questionIndex: number) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options.push('');
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options.splice(optionIndex, 1);
    setFormData({ ...formData, questions: newQuestions });
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === formData.questions.length - 1) return;

    const newQuestions = [...formData.questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    setFormData({ ...formData, questions: newQuestions });
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title={isEditing ? 'Edit Template' : 'Create Template'}
        description={isEditing ? 'Update your meeting template' : 'Create a reusable template for meetings'}
        action={
          <Button variant="outline" onClick={() => navigate('/templates')}>
            Cancel
          </Button>
        }
      />

      <Container>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Navigation Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {[
              { id: 'basic', label: 'Basic Info', icon: Clock },
              { id: 'meeting', label: 'Meeting Settings', icon: Video },
              { id: 'questions', label: 'Questions', icon: MessageSquare },
              { id: 'integrations', label: 'Integrations', icon: MessageSquare },
            ].map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <section.icon className="w-4 h-4 mr-2" />
                {section.label}
              </button>
            ))}
          </div>

          {/* Basic Info Section */}
          {activeSection === 'basic' && (
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Set the name and description for this template
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Discovery Call"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="discovery-call"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used in URLs: /templates/{formData.slug || 'your-slug'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe when this template should be used..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Meeting Settings Section */}
          {activeSection === 'meeting' && (
            <Card>
              <CardHeader>
                <CardTitle>Meeting Settings</CardTitle>
                <CardDescription>
                  Configure duration, buffers, and meeting links
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes) *
                    </label>
                    <input
                      type="number"
                      required
                      min={5}
                      max={240}
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Buffer Before (min)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={60}
                      value={formData.bufferBefore}
                      onChange={(e) => setFormData({ ...formData, bufferBefore: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Buffer After (min)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={60}
                      value={formData.bufferAfter}
                      onChange={(e) => setFormData({ ...formData, bufferAfter: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Auto-create Meeting Link
                      </label>
                      <p className="text-sm text-gray-500">
                        Automatically generate Google Meet or Zoom link
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, autoCreateMeetingLink: !formData.autoCreateMeetingLink })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.autoCreateMeetingLink ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.autoCreateMeetingLink ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {formData.autoCreateMeetingLink && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Meeting Provider
                      </label>
                      <select
                        value={formData.meetingProvider}
                        onChange={(e) => setFormData({ ...formData, meetingProvider: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="GOOGLE_MEET">Google Meet</option>
                        <option value="ZOOM">Zoom</option>
                      </select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions Section */}
          {activeSection === 'questions' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Pre-Meeting Questions</CardTitle>
                    <CardDescription>
                      Ask questions to qualify leads before the meeting
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    onClick={addQuestion}
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    Add Question
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.questions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No questions added yet. Click "Add Question" to get started.
                  </div>
                ) : (
                  formData.questions.map((question, index) => (
                    <div key={question.id} className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-start space-x-2">
                        <div className="flex flex-col space-y-1">
                          <button
                            type="button"
                            onClick={() => moveQuestion(index, 'up')}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveQuestion(index, 'down')}
                            disabled={index === formData.questions.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex-1 space-y-4">
                          <div className="flex items-center space-x-4">
                            <input
                              type="text"
                              value={question.question}
                              onChange={(e) => updateQuestion(index, { question: e.target.value })}
                              placeholder="Enter your question..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            
                            <select
                              value={question.type}
                              onChange={(e) => updateQuestion(index, { type: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              {QUESTION_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              onClick={() => removeQuestion(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="flex items-center space-x-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={question.isRequired}
                                onChange={(e) => updateQuestion(index, { isRequired: e.target.checked })}
                                className="mr-2"
                              />
                              Required
                            </label>

                            <select
                              value={question.crmField || ''}
                              onChange={(e) => updateQuestion(index, { crmField: e.target.value || undefined })}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-lg"
                            >
                              {CRM_FIELDS.map((field) => (
                                <option key={field.value} value={field.value}>
                                  {field.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {(question.type === 'SELECT' || question.type === 'MULTI_SELECT') && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-700">Options:</p>
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                    placeholder={`Option ${optIndex + 1}`}
                                    className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeOption(index, optIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <Button
                                type="button"
                                onClick={() => addOption(index)}
                                size="sm"
                                variant="outline"
                              >
                                Add Option
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {/* Integrations Section */}
          {activeSection === 'integrations' && (
            <Card>
              <CardHeader>
                <CardTitle>Integrations</CardTitle>
                <CardDescription>
                  Configure Discord notifications and other integrations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border-b pb-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                    Discord Notifications
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Webhook URL
                      </label>
                      <input
                        type="url"
                        value={formData.discordWebhookUrl}
                        onChange={(e) => setFormData({ ...formData, discordWebhookUrl: e.target.value })}
                        placeholder="https://discord.com/api/webhooks/..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get this from Discord Server Settings → Integrations → Webhooks
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Channel (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.discordChannel}
                        onChange={(e) => setFormData({ ...formData, discordChannel: e.target.value })}
                        placeholder="#sales-bookings"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">
                    More integrations (CRM, Slack, Notion) coming soon...
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/templates')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={saving}
            >
              {isEditing ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </form>
      </Container>
    </Layout>
  );
}
